/* eslint-disable max-lines */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Alert,
  Collapse,
  SwipeableDrawer
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Hearing as HearingIcon,
  Warning as WarningIcon,
  SmartToy as BotIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Note as NoteIcon,
  NoteAdd as NoteAddIcon,
  Menu as MenuIcon,
  Psychology as AgentIcon,
  StopCircle as StopCircleIcon,
  Tune as ManagePromptsIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import api from '../../services/api';
import { FaanaVoiceService } from '../../services/voice/FaanaVoiceService';
import TokenizedText from './TokenizedText';
import StreamingMessage from './StreamingMessage';
import { useNavigate } from 'react-router';
import PromptDisplay from './PromptDisplay';
import useMobileDetection from '../../hooks/useMobileDetection';

// Faana Logo Avatar component
const FaanaAvatar = ({ size = 40 }) => {
  const [logoError, setLogoError] = useState(false);

  return (
    <Avatar
      sx={{
        bgcolor: 'white',
        width: size,
        height: size,
        border: '2px solid #f0f0f0',
        padding: '4px',
        overflow: 'hidden',
      }}
    >
      {logoError ? (
        <BotIcon sx={{ color: 'primary.main' }} />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src="/faana_logo.png"
            alt="Faana"
            onError={() => setLogoError(true)}
            sx={{
              width: '70%',
              height: '70%',
              objectFit: 'contain',
            }}
          />
        </Box>
      )}
    </Avatar>
  );
};

// Cache for failed photo requests (1 hour timeout)
const photoFailureCache = new Map();
const PHOTO_FAILURE_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds

// User Avatar component that uses cached photos
const UserAvatar = ({ user, size = 40 }) => {
  const { user: currentUser } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.email && !imageError && currentUser && !loading) {
      // Check if we've recently failed to load this user's photo
      const cacheKey = user.email;
      const cachedFailure = photoFailureCache.get(cacheKey);

      if (cachedFailure && (Date.now() - cachedFailure) < PHOTO_FAILURE_TIMEOUT) {
        // Skip loading if we failed within the last hour
        setImageError(true);
        return;
      }

      const loadImage = async () => {
        setLoading(true);
        try {
          const token = await currentUser.getIdToken();
          const photoUrl = api.getUserPhotoUrl(user.email);
          const response = await fetch(photoUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            mode: 'cors',
            credentials: 'omit'
          });

          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageSrc(objectUrl);
            setImageError(false);
            // Clear any cached failure for this user
            photoFailureCache.delete(cacheKey);
          } else if (response.status === 404) {
            // Cache 404 failures to prevent retrying for 1 hour
            photoFailureCache.set(cacheKey, Date.now());
            setImageError(true);
          } else {
            setImageError(true);
          }
        } catch (_error) {
          // Cache network/other errors as well
          photoFailureCache.set(cacheKey, Date.now());
          setImageError(true);
          setImageSrc(null);
        } finally {
          setLoading(false);
        }
      };

      loadImage();
    }

    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, currentUser]);

  const getInitials = (user) => {
    if (user?.displayName) {
      const names = user.displayName.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[1][0]).toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Avatar sx={{ width: size, height: size, bgcolor: 'grey.300' }}>
        <CircularProgress size={size * 0.6} sx={{ color: 'grey.500' }} />
      </Avatar>
    );
  }

  if (imageSrc && !imageError) {
    return (
      <Avatar src={imageSrc} sx={{ width: size, height: size }}>
        {getInitials(user)}
      </Avatar>
    );
  }

  return (
    <Avatar sx={{
      width: size,
      height: size,
      bgcolor: 'secondary.main',
      color: 'white'
    }}>
      {getInitials(user)}
    </Avatar>
  );
};

// Component to display MCP tool details with expand/collapse
const MCPToolDetails = ({ tools }) => {
  const [expanded, setExpanded] = useState(false);

  if (!tools || tools.length === 0) return null;

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        onClick={() => setExpanded(!expanded)}
        startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          color: 'info.dark',
          fontSize: '0.75rem',
          p: 0,
          minWidth: 'auto',
          '&:hover': { bgcolor: 'transparent' }
        }}
      >
        Show {expanded ? 'less' : 'technical'} details
      </Button>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', display: 'block' }}>
            {JSON.stringify(tools.map(t => ({
              tool: t.tool,
              input: t.input,
              resourcesAccessed: t.resourcesAccessed
            })), null, 2)}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

function stopVoiceOnError(streamingVoiceActive, voiceService, activeMicMode, setVoiceStatus) {
  if (!streamingVoiceActive) return;
  try { voiceService.stopStreamingSpeech(); } catch (_) { /* intentionally empty */ }
  setVoiceStatus(activeMicMode ? 'listening' : 'idle');
}

/**
 * Replace raw token IDs (e.g. PER_1a702319a69a) with human-readable labels
 * for display in contexts where calling the detokenize API isn't practical
 * (chat list previews, TTS, etc.)
 */
const TOKEN_LABELS = {
  PER: 'Person', ORG: 'Organization', LOC: 'Location', ADR: 'Address',
  DTE: 'Date', DOB: 'Date', EML: 'Email', PHN: 'Phone', CCN: 'Card',
  SSN: 'SSN', DMG: 'Info', CRD: 'Credential', DIG: 'ID', GID: 'ID',
  MED: 'Medical', TKN: 'Token',
};
const stripTokens = (text) =>
  text ? text.replace(/\b([A-Z]{2,4})_[a-f0-9]{12}\b/g, (_, prefix) =>
    `[${TOKEN_LABELS[prefix] || 'Redacted'}]`) : '';

// eslint-disable-next-line max-lines-per-function
const ChatBot = ({ entityId, entityName, fullPage = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings: userPreferences } = useUserPreferences();
  const { isMobile } = useMobileDetection();
  const [open, setOpen] = useState(fullPage);
  const [chatListCollapsed, setChatListCollapsed] = useState(() => {
    // Load saved preference from localStorage
    const savedState = localStorage.getItem('chatListCollapsed');
    return savedState !== null ? JSON.parse(savedState) : true; // Default to collapsed
  });
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [renamingChat, setRenamingChat] = useState(null);
  const [newChatTitle, setNewChatTitle] = useState('');
  const [error, setError] = useState(null);
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [showPromptSelector, setShowPromptSelector] = useState(false);
  const [noteTakingMode, setNoteTakingMode] = useState(false);
  const [_currentNoteId, setCurrentNoteId] = useState(null);

  // Streaming states
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingToolActivity, setStreamingToolActivity] = useState([]);
  const streamAbortRef = useRef(null);
  const streamContentRef = useRef('');
  const voiceContentRef = useRef('');
  const toolsInvokedRef = useRef(false);
  const streamingUpdateTimer = useRef(null);

  // Voice states
  const [activeMicMode, setActiveMicMode] = useState(false);
  const [voiceResponseMode, setVoiceResponseMode] = useState(false);
  const [voiceService, setVoiceService] = useState(null);
  const [_isRecording, setIsRecording] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [showChromeWarning, setShowChromeWarning] = useState(false);
  const [voiceTranscriptBuffer, setVoiceTranscriptBuffer] = useState('');
  const messagesEndRef = useRef(null);
  const sendMessageRef = useRef(null);

  // Cleanup voice service on unmount — releases mic and stops recognition
  useEffect(() => {
    return () => {
      if (voiceService) {
        voiceService.stop();
        voiceService.stopSpeaking();
      }
    };
  }, [voiceService]);

  // Auto-collapse chat list on mobile
  useEffect(() => {
    setChatListCollapsed(isMobile);
  }, [isMobile]);

  // Function to toggle message collapse state
  const toggleMessageCollapse = (index) => {
    setMessages(prev => prev.map((msg, i) =>
      i === index ? { ...msg, isCollapsed: !msg.isCollapsed } : msg
    ));
  };

  // Function to toggle MCP logs collapse state
  const toggleMcpLogsCollapse = (index) => {
    setMessages(prev => prev.map((msg, i) =>
      i === index ? { ...msg, mcpLogsCollapsed: !msg.mcpLogsCollapsed } : msg
    ));
  };

  // Debug logging

  // Load chats when entity changes
  useEffect(() => {
    console.log('Load chats effect - entityId:', entityId, 'open:', open);
    if (entityId && open) {
      loadChats();
      loadAvailablePrompts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, open]);

  // Load chat history when active chat changes
  useEffect(() => {
    console.log('useEffect triggered - activeChat:', activeChat, 'entityId:', entityId);
    if (!activeChat || !entityId) {
      console.log('Conditions not met - activeChat:', !!activeChat, 'entityId:', !!entityId);
      return;
    }

    console.log('Conditions met, loading chat history for:', activeChat);

    const loadHistory = async () => {
      try {
        setLoading(true);
        setMessages([]); // Clear messages immediately for better UX
        console.log('Making API call for chat history:', { entityId, activeChat });
        const response = await api.getChatHistory(entityId, activeChat);
        console.log('Chat history response:', response);
        setMessages(response.messages || []);

        // Restore note-taking state from chat metadata
        // Each chat maintains its own note-taking state
        try {
          // Get chat metadata to restore note-taking state
          const chatMetadata = await api.getChatMetadata(entityId, activeChat);
          if (chatMetadata) {
            setNoteTakingMode(chatMetadata.isNoteTakingActive || false);
            setCurrentNoteId(chatMetadata.noteId || null);
          } else {
            // Fallback: reset state if no metadata found
            setNoteTakingMode(false);
            setCurrentNoteId(null);
          }
        } catch (metaError) {
          console.warn('Failed to load chat metadata, resetting note state:', metaError);
          setNoteTakingMode(false);
          setCurrentNoteId(null);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
        setError('Failed to load messages');
        setMessages([]); // Clear on error too
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [activeChat, entityId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChats = async () => {
    try {
      setLoading(true);
      const response = await api.listChats(entityId);
      setChats(response.chats || []);

      // If no active chat and chats exist, select the first one
      if (!activeChat && response.chats?.length > 0) {
        console.log('No active chat, selecting first:', response.chats[0].chatId);
        setActiveChat(response.chats[0].chatId);
      }
      // else: no chats to select — no action needed
    } catch (error) {
      console.error('Failed to load chats:', error);
      setError('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePrompts = async () => {
    try {
      setLoadingPrompts(true);
      const response = await api.listPrompts({ category: 'chat', isActive: true });
      console.log('Loaded prompts:', response.prompts);
      setAvailablePrompts(response.prompts || []);

      // Set default prompt if none selected
      if (!selectedPrompt && response.prompts?.length > 0) {
        const defaultPrompt = response.prompts.find(p => p.isDefault) || response.prompts[0];
        setSelectedPrompt(defaultPrompt);
      }
    } catch (error) {
      console.error('Failed to load prompts:', error);
      // Don't show error to user as this is optional functionality
    } finally {
      setLoadingPrompts(false);
    }
  };

  const createNewChat = async () => {
    try {
      const response = await api.createChat(entityId, `Chat ${new Date().toLocaleDateString()}`);
      await loadChats();
      setActiveChat(response.chatId);
      setMessages([]);
    } catch (error) {
      console.error('Failed to create chat:', error);
      setError('Failed to create new chat');
    }
  };

  // Flush streaming content to state (batched updates)
  const flushStreamContent = useCallback(() => {
    setStreamingContent(streamContentRef.current);
  }, []);

  // Stop streaming handler
  const handleStopStreaming = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
    const finalContent = streamContentRef.current;
    const msgId = streamingMessageId;
    setStreamingMessageId(null);
    setStreamingToolActivity([]);
    // Finalize voice with whatever has arrived so far
    if (voiceResponseMode && voiceService && typeof voiceService.finalizeStreamingSpeech === 'function' && finalContent) {
      try { voiceService.finalizeStreamingSpeech(finalContent); } catch (_) { /* intentionally empty */ }
    }
    // Keep whatever content has arrived
    if (finalContent && msgId) {
      setMessages(prev => prev.map(msg =>
        msg.id === msgId ? {
          ...msg,
          content: finalContent,
          loading: false,
          streaming: false,
        } : msg
      ));
    }
    streamContentRef.current = '';
    voiceContentRef.current = '';
    setSendingMessage(false);
  }, [streamingMessageId, voiceResponseMode, voiceService]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-lines-per-function
  const sendMessage = async () => {
    if (!inputMessage.trim() || sendingMessage) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
    };

    // Create a placeholder ID outside the try block
    const placeholderId = `placeholder_${Date.now()}`;

    try {
      setSendingMessage(true);
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');

      // Create a placeholder for the AI response
      setMessages(prev => [...prev, {
          id: placeholderId,
          role: 'assistant',
          content: '',
          loading: true,
        }]);

        // Include selected prompt ID and note-taking mode in the request
        const promptId = selectedPrompt?.id;

        // Use SSE streaming for progressive token delivery
        setStreamingMessageId(placeholderId);
        setStreamingContent('');
        streamContentRef.current = '';
        voiceContentRef.current = '';
        toolsInvokedRef.current = false;

        // Start progressive voice streaming if voice response is enabled
        let streamingVoiceActive = false;
        if (voiceResponseMode && voiceService && typeof voiceService.beginStreamingSpeech === 'function') {
          try {
            // flushSync forces React to paint the "Generating speech..." banner
            // immediately, before streaming begins and floods the render queue.
            flushSync(() => setVoiceStatus('generating'));
            voiceService.callbacks.onSpeakingStart = () => setVoiceStatus('speaking');
            voiceService.callbacks.onSpeakingEnd = () => setVoiceStatus(activeMicMode ? 'listening' : 'idle');
            streamingVoiceActive = voiceService.beginStreamingSpeech() === true;
            // eslint-disable-next-line max-depth
            if (streamingVoiceActive) {
              lastSpokenMessageRef.current = placeholderId; // prevent re-speak in useEffect
            }
          } catch (voiceErr) {
            console.warn('[Voice] Failed to start streaming speech:', voiceErr);
            streamingVoiceActive = false;
          }
        }

        try {
          const { abort } = await api.streamChatMessage(
            entityId,
            activeChat,
            userMessage.content,
            { promptId, noteTakingMode },
            {
              onToken: (token) => {
                streamContentRef.current += token;
                voiceContentRef.current += token;
                // Batch UI updates using requestAnimationFrame
                if (!streamingUpdateTimer.current) {
                  streamingUpdateTimer.current = requestAnimationFrame(() => {
                    flushStreamContent();
                    streamingUpdateTimer.current = null;
                  });
                }
                // Feed voice from voiceContentRef (skipped once tools are invoked —
                // onDone will speak the complete response in one clean pass)
                if (streamingVoiceActive && !toolsInvokedRef.current) {
                  try {
                    voiceService.feedStreamingText(voiceContentRef.current);
                  } catch (feedErr) {
                    console.error('[VOICE-FEED-ERROR]', feedErr.message);
                  }
                }
              },
              onToolStart: (toolName) => {
                setStreamingToolActivity(prev => [...prev, { toolName, status: 'running', startedAt: Date.now() }]);
                // Mark tools as invoked — voice will defer to onDone for complete response
                toolsInvokedRef.current = true;
                voiceContentRef.current = '';
                if (streamingVoiceActive) {
                  try { voiceService.stopStreamingSpeech(); } catch (_) { /* intentionally empty */ }
                }
              },
              onToolEnd: (data) => {
                setStreamingToolActivity(prev => prev.map(t =>
                  t.status === 'running' && (!data?.toolName || t.toolName === data.toolName)
                    ? { ...t, status: 'done', completedAt: Date.now() }
                    : t
                ));
                // Voice deferred to onDone — no restart between tool calls
              },
              onError: (errorData) => {
                setStreamingMessageId(null);
                setStreamingToolActivity([]);
                streamContentRef.current = '';
                voiceContentRef.current = '';
                // Stop any in-progress streaming speech on error
                if (streamingVoiceActive) {
                  try { voiceService.stopStreamingSpeech(); } catch (_) { /* intentionally empty */ }
                  setVoiceStatus(activeMicMode ? 'listening' : 'idle');
                }
                setMessages(prev => prev.map(msg =>
                  msg.id === placeholderId ? {
                    ...msg,
                    content: errorData.userMessage || errorData.message || 'An unexpected error occurred.',
                    loading: false,
                  } : msg
                ));
                setSendingMessage(false);
              },
              onDone: (data) => {
                const doneTimestamp = new Date().toISOString();

                // Voice: if tools were used, progressive voicing was skipped —
                // speak the complete response now. Otherwise finalize progressive stream.
                if (streamingVoiceActive) {
                  try {
                    if (toolsInvokedRef.current) {
                      voiceService.speak(data.output || streamContentRef.current);
                    } else {
                      voiceService.finalizeStreamingSpeech(data.output || streamContentRef.current);
                    }
                  } catch (_) { /* intentionally empty */ }
                  lastSpokenMessageRef.current = doneTimestamp;
                }

                // Final update with complete content
                setStreamingMessageId(null);
                setStreamingToolActivity([]);
                setMessages(prev => prev.map(msg =>
                  msg.id === placeholderId ? {
                    ...msg,
                    role: 'assistant',
                    content: data.output || streamContentRef.current,
                    loading: false,
                    streaming: false,
                    toolsUsed: data.toolsUsed || [],
                    timestamp: doneTimestamp,
                  } : msg
                ));
                streamContentRef.current = '';
                voiceContentRef.current = '';
                setSendingMessage(false);

                // Update note-taking state if changed
                if (data.noteId) {
                  setCurrentNoteId(data.noteId);
                }

                // Check if AI used toggle_note_taking tool
                if (data.toolsUsed) {
                  for (const tool of data.toolsUsed) {
                    if (tool.tool === 'toggle_note_taking') {
                      // eslint-disable-next-line max-depth
                      try {
                        const toolResponse = JSON.parse(tool.observation || '{}');
                        // eslint-disable-next-line max-depth
                        if (toolResponse.noteTakingMode !== undefined) {
                          setNoteTakingMode(toolResponse.noteTakingMode);
                        }
                      } catch (e) {
                        console.error('Failed to parse toggle_note_taking response:', e);
                      }
                    }
                  }
                }

                // Update chat list to reflect new message
                loadChats();

                // After the 3rd user message, the backend auto-generates a title.
                const userMessageCount = messages.filter(m => m.role === 'user').length + 1;
                if (userMessageCount === 3) {
                  setTimeout(async () => {
                    await loadChats();
                  }, 2000);
                }
              },
            }
          );

          streamAbortRef.current = { abort };
        } catch (streamError) {
          // F14: Stream connection failed — show inline error (no batch fallback)
          console.error('Stream connection failed:', streamError);
          setStreamingMessageId(null);
          setStreamingToolActivity([]);
          streamContentRef.current = '';
          voiceContentRef.current = '';
          // Stop any in-progress streaming speech on error
          stopVoiceOnError(streamingVoiceActive, voiceService, activeMicMode, setVoiceStatus);

          setMessages(prev => prev.map(msg =>
            msg.id === placeholderId ? {
              ...msg,
              content: streamError?.message || 'Failed to connect to chat service. Please try again.',
              loading: false,
            } : msg
          ));
          setSendingMessage(false);
        }
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('Failed to send message');
      // Remove the placeholder message
      setMessages(prev => prev.filter(msg => msg.id !== placeholderId));
      setStreamingMessageId(null);
      setStreamingToolActivity([]);
      streamContentRef.current = '';
      voiceContentRef.current = '';
    } finally {
      setSendingMessage(false);
    }
  };

  // Voice Control Handlers
  // eslint-disable-next-line max-lines-per-function
  const handleActiveMicToggle = async () => {
    // Check if browser supports SpeechRecognition API
    if (!('SpeechRecognition' in window) && !('webkitSpeechRecognition' in window)) {
      setShowChromeWarning(true);
      return;
    }

    if (!activeMicMode) {
      // Starting Active Mic mode
      setActiveMicMode(true);
      setVoiceStatus('initializing');

      // Build callbacks once — used for both new and existing service
      const voiceCallbacks = {
        onWakeWord: () => {
          setVoiceStatus('recording');
          setIsRecording(true);
          toast.success('Wake word detected! Start speaking...', {
            autoClose: 2000,
            position: 'top-center'
          });
        },
        onRecordingStart: () => {
          setVoiceStatus('recording');
        },
        onTranscript: (text, isFinal) => {
          if (isFinal && text) {
            setInputMessage(prev => {
              const newText = prev ? prev + ' ' + text : text;
              return newText;
            });
            setVoiceTranscriptBuffer('');
          }
        },
        onInterim: (text) => {
          setVoiceTranscriptBuffer(text);
        },
        onSendCommand: (remainingText) => {
          if (remainingText) {
            setInputMessage(prev => {
              const newText = prev ? prev + ' ' + remainingText : remainingText;
              return newText;
            });
          }
          setVoiceTranscriptBuffer('');
          setTimeout(() => {
            if (sendMessageRef.current) {
              sendMessageRef.current();
            } else {
              console.error('[ChatBot] sendMessageRef.current is null!');
            }
          }, 100);
          toast.info('Sending message...', { autoClose: 2000 });
        },
        onClearCommand: () => {
          setInputMessage('');
          setVoiceTranscriptBuffer('');
          toast.info('Message cleared', { autoClose: 2000 });
        },
        onStopRecording: () => {
          setIsRecording(false);
          setVoiceStatus('listening');
        },
        onError: (error) => {
          console.error('Voice error:', error);
          if (error === 'speech-recognition-unavailable') {
            setActiveMicMode(false);
            setVoiceStatus('idle');
            setShowChromeWarning(true);
            return;
          }
          setVoiceStatus('error');
          toast.error(`Voice error: ${error}`, { autoClose: 5000 });
        },
        onLowBattery: () => {
          setActiveMicMode(false);
          setVoiceStatus('idle');
          toast.warning('Voice disabled: Battery level below 20%', { autoClose: 5000 });
        }
      };

      // Initialize voice service if not already done
      const service = voiceService || new FaanaVoiceService(userPreferences.voice);
      if (!voiceService) {
        setVoiceService(service);
        // Mark current message as spoken to prevent re-speaking when Voice Response is later enabled
        const lastMessage = messages[messages.length - 1];
        if (lastMessage?.role === 'assistant' && lastMessage.timestamp) {
          lastSpokenMessageRef.current = lastMessage.timestamp;
        }
      }

      // Start with callbacks — synchronous, no timers needed
      const started = service.start(voiceCallbacks);
      if (started) {
        setVoiceStatus('listening');
        toast.success('Active Mic enabled - Say "Hey Coach" to start', {
          autoClose: 3000,
          position: 'top-center'
        });
      } else {
        setActiveMicMode(false);
        setVoiceStatus('idle');
        toast.error('Failed to enable voice recognition', { autoClose: 5000 });
      }
    } else {
      // Stopping Active Mic mode
      setActiveMicMode(false);
      setIsRecording(false);
      setVoiceStatus('idle');
      setVoiceTranscriptBuffer('');

      if (voiceService) {
        voiceService.stop();
      }

      toast.info('Active Mic disabled', { autoClose: 2000 });
    }
  };

  const handleVoiceResponseToggle = () => {
    const newMode = !voiceResponseMode;
    setVoiceResponseMode(newMode);

    // Initialize voice service if needed for TTS
    if (newMode && !voiceService) {
      const service = new FaanaVoiceService(userPreferences.voice);
      setVoiceService(service);

      // When enabling, mark the current last message as already spoken to avoid speaking old messages
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.role === 'assistant' && lastMessage.timestamp) {
        lastSpokenMessageRef.current = lastMessage.timestamp;
      }
    }

    // If turning off while speaking, stop speech
    if (voiceResponseMode && voiceService) {
      voiceService.stopSpeaking();
      // Clear the spoken message tracker when disabling
      lastSpokenMessageRef.current = null;
    }

    toast.info(newMode ? 'Voice Response enabled - AI will speak responses' : 'Voice Response disabled', {
      autoClose: 2000
    });
  };

  // Track the last spoken message to avoid re-speaking
  const lastSpokenMessageRef = useRef(null);

  // Handle voice response when AI responds
  useEffect(() => {
    if (!voiceResponseMode || !voiceService || !userPreferences?.voice?.tts?.enabled) {
      return;
    }

    // Find the latest assistant message
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant' && lastMessage.content && !lastMessage.loading) {
      // Check if we've already spoken this message
      if (lastSpokenMessageRef.current === lastMessage.timestamp) {
        // Only log once per message, not on every render
        return;
      }
      console.log('[VOICE-USEEFFECT] Found unspoken message — lastSpokenRef:', lastSpokenMessageRef.current, 'msg.timestamp:', lastMessage.timestamp, 'msg.id:', lastMessage.id, 'loading:', lastMessage.loading);

      // Extract text content, replacing tokens with type labels and removing formatting
      const textContent = stripTokens(lastMessage.content)
        .replace(/```[\s\S]*?```/g, '') // Remove code blocks
        .replace(/[*_~`]/g, '') // Remove markdown formatting
        .trim();

      if (textContent) {
        console.log('[Voice] Speaking text:', textContent.substring(0, 100) + '...');

        // Mark this message as spoken
        lastSpokenMessageRef.current = lastMessage.timestamp;

        // Set 'generating' immediately — visible during 1-3s Kokoro synthesis gap
        setVoiceStatus('generating');

        // Set callbacks for speaking events
        voiceService.callbacks.onSpeakingStart = () => {
          console.log('[Voice] Started speaking');
          setVoiceStatus('speaking');
        };
        voiceService.callbacks.onSpeakingEnd = () => {
          console.log('[Voice] Finished speaking');
          setVoiceStatus(activeMicMode ? 'listening' : 'idle');
        };

        // Speak with user's TTS settings
        voiceService.speak(textContent);
      }
    }
  }, [messages, voiceResponseMode, voiceService, userPreferences, activeMicMode]);

  // Update voice service when preferences change
  useEffect(() => {
    if (voiceService && userPreferences?.voice) {
      voiceService.updateSettings(userPreferences.voice);
    }
  }, [userPreferences?.voice, voiceService]);

  // Store sendMessage in ref for use in callbacks
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  const handleDeleteChat = async () => {
    if (!selectedChat) return;

    try {
      await api.deleteChat(entityId, selectedChat);

      // If deleting active chat, clear it
      if (activeChat === selectedChat) {
        setActiveChat(null);
        setMessages([]);
      }

      await loadChats();
      setChatMenuAnchor(null);
      setSelectedChat(null);
    } catch (error) {
      console.error('Failed to delete chat:', error);
      setError('Failed to delete chat');
    }
  };

  const handleRenameChat = async () => {
    if (!renamingChat || !newChatTitle.trim()) return;

    try {
      await api.updateChatTitle(entityId, renamingChat, newChatTitle);
      await loadChats();
      setRenamingChat(null);
      setNewChatTitle('');
    } catch (error) {
      console.error('Failed to rename chat:', error);
      setError('Failed to rename chat');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return format(new Date(timestamp), 'h:mm a');
    } catch {
      return '';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Interface - render as Box in fullPage mode, SwipeableDrawer otherwise */}
      {(() => {
        const chatContent = (
        <>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          ...(fullPage && { margin: 0, padding: 0 })
        }}>
          {/* Header - Simple gray bar on mobile, full AppBar on desktop */}
          {isMobile ? (
            // Mobile: Simple light gray header with clock icon and "Faana" text
            (<Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                bgcolor: 'grey.100',
                borderBottom: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <Box
                onClick={() => {
                  const newState = !chatListCollapsed;
                  setChatListCollapsed(newState);
                  localStorage.setItem('chatListCollapsed', JSON.stringify(newState));
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
                role="button"
                aria-label="toggle chat history"
                tabIndex={0}
              >
                <HistoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Faana
                </Typography>
              </Box>
            </Box>)
          ) : (
            // Desktop: Full purple AppBar header
            (<AppBar
              position="relative"
              color="primary"
              elevation={2}
              sx={{
                flexShrink: 0,
                minHeight: 64,
                width: '100%',
                bgcolor: 'primary.main',
                color: 'white'
              }}
            >
              <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
                {/* Toggle button for chat history */}
                <Tooltip title={chatListCollapsed ? "Show chat history" : "Hide chat history"}>
                  <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="toggle chat history"
                    onClick={() => {
                      const newState = !chatListCollapsed;
                      setChatListCollapsed(newState);
                      localStorage.setItem('chatListCollapsed', JSON.stringify(newState));
                    }}
                    sx={{
                      mr: 1,
                      bgcolor: !chatListCollapsed ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: !chatListCollapsed ? 'action.selected' : 'action.hover'
                      }
                    }}
                  >
                    {chatListCollapsed ? <MenuIcon /> : <CloseIcon />}
                  </IconButton>
                </Tooltip>

                {/* Avatar */}
                <Box sx={{ mr: 2 }}>
                  <FaanaAvatar size={40} />
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      Faana Growth OS AI
                    </Typography>
                    {selectedPrompt && (
                      <Chip
                        label={selectedPrompt.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: 'white',
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    {entityName || 'Select an entity'} • Neuroscience-based growth coaching
                  </Typography>
                </Box>

              {/* Desktop Toolbar Actions - Keep in header for desktop */}
              {!isMobile && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  {/* Voice Controls - Active Mic Mode */}
                  {userPreferences?.voice?.enabled && (
                  <Tooltip title={activeMicMode ? "Active Mic is ON" : "Enable Active Mic (Always listening for 'Hey Coach')"}>
                    <IconButton
                      onClick={() => handleActiveMicToggle()}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: activeMicMode ? 'white' : 'white',
                        bgcolor: activeMicMode ? 'rgba(255, 0, 0, 0.3)' : 'transparent',
                        animation: activeMicMode ? 'pulse-red 2s infinite' : 'none',
                        padding: isMobile ? '4px' : '8px',
                        '&:hover': {
                          bgcolor: activeMicMode ? 'rgba(255, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.1)'
                        },
                        '@keyframes pulse-red': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.7)',
                          },
                          '70%': {
                            boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)',
                          },
                        },
                      }}
                    >
                      {activeMicMode ? <MicIcon sx={{ fontSize: 24 }} /> : <MicOffIcon sx={{ fontSize: 24 }} />}
                    </IconButton>
                  </Tooltip>
                )}

                  {/* Voice Controls - Voice Response Mode */}
                  {userPreferences?.voice?.enabled && (
                  <Tooltip title={voiceResponseMode ? "Voice Response is ON" : "Enable Voice Response (AI speaks responses)"}>
                    <IconButton
                      onClick={() => handleVoiceResponseToggle()}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: voiceResponseMode ? 'white' : 'white',
                        bgcolor: voiceResponseMode ? 'rgba(33, 150, 243, 0.3)' : 'transparent',
                        animation: voiceResponseMode ? 'pulse-blue 2s infinite' : 'none',
                        padding: isMobile ? '4px' : '8px',
                        '&:hover': {
                          bgcolor: voiceResponseMode ? 'rgba(33, 150, 243, 0.4)' : 'rgba(255, 255, 255, 0.1)'
                        },
                        '@keyframes pulse-blue': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.7)',
                          },
                          '70%': {
                            boxShadow: '0 0 0 10px rgba(33, 150, 243, 0)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)',
                          },
                        },
                      }}
                    >
                      {voiceResponseMode ? <VolumeUpIcon sx={{ fontSize: 24 }} /> : <VolumeOffIcon sx={{ fontSize: 24 }} />}
                    </IconButton>
                  </Tooltip>
                )}

                  {/* Note-taking Mode Toggle */}
                  <Tooltip title={noteTakingMode ? "Note-taking is active" : "Enable note-taking"}>
                    <IconButton
                      onClick={() => setNoteTakingMode(!noteTakingMode)}
                      sx={{
                        color: noteTakingMode ? '#ffeb3b' : 'white',
                        bgcolor: noteTakingMode ? 'rgba(255, 235, 59, 0.1)' : 'transparent',
                        '&:hover': {
                          bgcolor: noteTakingMode ? 'rgba(255, 235, 59, 0.2)' : 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      {noteTakingMode ? <NoteIcon /> : <NoteAddIcon />}
                    </IconButton>
                  </Tooltip>
                  {/* Prompt Selector */}
                  {availablePrompts.length > 0 && (
                  <Tooltip title="Select AI Prompt">
                    <IconButton
                      onClick={() => setShowPromptSelector(true)}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: 'white',
                        padding: isMobile ? '4px' : '8px',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <AgentIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
                  {/* Manage Prompts */}
                  <Tooltip title="Manage Prompts">
                    <IconButton
                      onClick={() => navigate('/faana-agents')}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: 'white',
                        padding: isMobile ? '4px' : '8px',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <ManagePromptsIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  {/* Close button - only show when not in fullPage mode */}
                  {!fullPage && (
                    <Tooltip title="Hide chat">
                      <IconButton
                        onClick={() => setOpen(false)}
                        sx={{
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                        aria-label="hide chat"
                      >
                        <CloseIcon sx={{ fontSize: 24 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
              </Toolbar>
            </AppBar>)
          )}

          {/* Main Content Area */}
          <Box sx={{
            display: 'flex',
            flexGrow: 1,
            overflow: 'hidden',
            position: 'relative',
            ...(fullPage && { margin: 0, padding: 0 })
          }}>
            {/* Chat List Sidebar - Collapsible */}
            <Paper
              elevation={0}
              sx={{
                width: chatListCollapsed ? 0 : (isMobile ? '280px' : '240px'),
                minWidth: chatListCollapsed ? 0 : (isMobile ? '280px' : '240px'),
                borderRight: chatListCollapsed ? 0 : 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                position: 'relative',
                height: '100%',
                bgcolor: 'background.paper',
                boxShadow: chatListCollapsed ? 0 : (isMobile ? 3 : 1),
              }}
            >
              <Box sx={{ p: 1, display: chatListCollapsed ? 'none' : 'block' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Chat History
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size={isMobile ? "small" : "medium"}
                  onClick={createNewChat}
                  disabled={!entityId}
                >
                  New Chat
                </Button>
              </Box>
              <Divider sx={{ display: chatListCollapsed ? 'none' : 'block' }} />
              <List sx={{
                flexGrow: 1,
                overflow: 'auto',
                display: chatListCollapsed ? 'none' : 'block',
                maxHeight: 'calc(100vh - 200px)',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'background.paper',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'divider',
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                },
              }}>
                {loading && !chats.length ? (
                  Array.from(new Array(3)).map((_, index) => (
                    <ListItem key={index}>
                      <Skeleton variant="text" width="100%" />
                    </ListItem>
                  ))
                ) : (
                  chats.map((chat) => (
                    <ListItem
                      key={chat.chatId}
                      component="div"
                      selected={activeChat === chat.chatId}
                      onClick={() => {
                        console.log('Chat clicked:', chat.chatId);
                        console.log('Current activeChat:', activeChat);
                        if (chat.chatId !== activeChat) {
                          console.log('Setting activeChat to:', chat.chatId);
                          setActiveChat(chat.chatId);
                        } else {
                          console.log('Chat already active, forcing reload');
                          // Force reload the chat history
                          const loadHistory = async () => {
                            try {
                              setLoading(true);
                              setMessages([]);
                              console.log('Force reloading chat history:', { entityId, activeChat: chat.chatId });
                              const response = await api.getChatHistory(entityId, chat.chatId);
                              console.log('Forced reload response:', response);
                              setMessages(response.messages || []);
                            } catch (error) {
                              console.error('Failed to reload chat history:', error);
                              setError('Failed to reload messages');
                              setMessages([]);
                            } finally {
                              setLoading(false);
                            }
                          };
                          loadHistory();
                        }
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setChatMenuAnchor(e.currentTarget);
                            setSelectedChat(chat.chatId);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={chat.title}
                        secondary={chat.lastMessage ? stripTokens(chat.lastMessage).substring(0, 30) + '...' : 'No messages'}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>

            {/* Chat Messages Area */}
            <Box sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              ...(fullPage && { margin: 0, padding: 0 })
            }}>
              {!entityId ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Alert severity="info">
                    Please select an entity to start chatting
                  </Alert>
                </Box>
              ) : !activeChat ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Select a chat or create a new one to begin
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Messages */}
                  <Box sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    p: 2,
                    ...(fullPage && { height: '100%' })
                  }}>
                    {/* Debug info */}
                    {messages.length === 0 && !loading && (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No messages in this chat yet. Send a message to start the conversation.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Chat ID: {activeChat}
                        </Typography>
                      </Box>
                    )}
                    {loading && !messages.length ? (
                      Array.from(new Array(3)).map((_, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Skeleton variant="rectangular" height={60} />
                        </Box>
                      ))
                    ) : (
                      // eslint-disable-next-line max-lines-per-function
                      (messages.map((message, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                            mb: 2,
                          }}
                        >
                          {message.role === 'assistant' && (
                            <Box sx={{ mr: 1 }}>
                              <FaanaAvatar size={40} />
                            </Box>
                          )}
                          <Paper
                            elevation={1}
                            sx={{
                              p: 2,
                              maxWidth: '70%',
                              bgcolor: message.role === 'user' ? 'primary.main' : 'grey.100',
                              color: message.role === 'user' ? 'white' : 'text.primary',
                            }}
                          >
                            {message.id === streamingMessageId ? (
                              <StreamingMessage
                                content={streamingContent}
                                isStreaming={true}
                                toolActivity={streamingToolActivity}
                              />
                            ) : message.loading ? (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <CircularProgress size={16} color={message.role === 'user' ? 'inherit' : 'primary'} />
                                  <Typography variant="body2">
                                    {message.content || 'Thinking...'}
                                  </Typography>
                                </Box>
                                {/* Real-time MCP Logs for loading messages */}
                                {message.mcpLogs && message.mcpLogs.length > 0 && (
                                  <Box
                                    sx={{
                                      mt: 1,
                                      p: 1,
                                      bgcolor: 'action.hover',
                                      borderRadius: 1,
                                      maxHeight: 120,
                                      overflow: 'auto'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                        🔧 Live Progress Updates:
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleMcpLogsCollapse(index)}
                                        sx={{ color: 'text.secondary', p: 0.25 }}
                                      >
                                        {message.mcpLogsCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                                      </IconButton>
                                    </Box>
                                    <Collapse in={!message.mcpLogsCollapsed}>
                                      {message.mcpLogs.slice(-8).map((log, logIndex) => (
                                        <Typography key={logIndex} variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.2 }}>
                                          {new Date(log.timestamp).toLocaleTimeString()} - {log.tool}: {log.data?.query || log.data?.entityId || log.data?.operation || 'processing...'}
                                        </Typography>
                                      ))}
                                      {message.mcpLogs.length > 8 && (
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', fontSize: '0.65rem' }}>
                                          +{message.mcpLogs.length - 8} more operations...
                                        </Typography>
                                      )}
                                    </Collapse>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <>
                                {/* Collapsible message content */}
                                {message.isCollapsible ? (
                                  <Box>
                                    {message.isCollapsed ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                          <TokenizedText
                                            content={message.contentSummary || message.content.split('\n')[0]}
                                            entityId={entityId}
                                            variant="body2"
                                          />
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={() => toggleMessageCollapse(index)}
                                          sx={{ color: message.role === 'user' ? 'white' : 'inherit' }}
                                        >
                                          <ExpandMoreIcon />
                                        </IconButton>
                                      </Box>
                                    ) : (
                                      <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                          <Box sx={{ flex: 1 }}>
                                            <TokenizedText
                                              content={message.content}
                                              entityId={entityId}
                                              variant="body2"
                                              sx={{ whiteSpace: 'pre-wrap' }}
                                            />
                                          </Box>
                                          <IconButton
                                            size="small"
                                            onClick={() => toggleMessageCollapse(index)}
                                            sx={{ color: message.role === 'user' ? 'white' : 'inherit' }}
                                          >
                                            <ExpandLessIcon />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                ) : (
                                  message.role === 'assistant' ? (
                                    <TokenizedText
                                      content={message.content}
                                      entityId={entityId}
                                      variant="body2"
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                    />
                                  ) : (
                                    <TokenizedText
                                      content={message.content}
                                      entityId={entityId}
                                      variant="body2"
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                    />
                                  )
                                )}

                                {/* MCP Tool Usage Summary - Displayed Above Response */}
                                {message.toolsUsed && message.toolsUsed.length > 0 && (
                                  <Box
                                    sx={{
                                      mb: 2,
                                      p: 1.5,
                                      bgcolor: 'info.light',
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'info.main',
                                      opacity: 0.9
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.dark', display: 'block', mb: 1 }}>
                                      🔍 AI Actions Performed:
                                    </Typography>
                                    {message.toolsUsed.map((tool, toolIndex) => (
                                      <Box key={toolIndex} sx={{ mb: 0.5 }}>
                                        {tool.tool === 'list_entity_resources' && (
                                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 500 }}>
                                              📋 Listed available resources
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                              ({tool.resourcesAccessed.length} items found)
                                            </Typography>
                                          </Box>
                                        )}
                                        {tool.tool === 'search_tokenized_documents' && (
                                          <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                              <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 500 }}>
                                                🔎 Searched documents
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                for "{typeof tool.input === 'string' ? JSON.parse(tool.input).query : tool.input.query}"
                                              </Typography>
                                            </Box>
                                            {tool.resourcesAccessed.length > 0 && (
                                              <Box sx={{ ml: 3, mt: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                  Found {tool.resourcesAccessed.length} matching document{tool.resourcesAccessed.length > 1 ? 's' : ''}:
                                                </Typography>
                                                {tool.resourcesAccessed.slice(0, 3).map((resource, idx) => (
                                                  <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'info.dark', ml: 1 }}>
                                                    • {resource.name}
                                                  </Typography>
                                                ))}
                                                {tool.resourcesAccessed.length > 3 && (
                                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', ml: 1 }}>
                                                    • ...and {tool.resourcesAccessed.length - 3} more
                                                  </Typography>
                                                )}
                                              </Box>
                                            )}
                                          </Box>
                                        )}
                                        {tool.tool === 'read_entity_resource' && tool.resourcesAccessed.length > 0 && (
                                          <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                              <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 500 }}>
                                                📄 Read document
                                              </Typography>
                                            </Box>
                                            <Box sx={{ ml: 3, mt: 0.5 }}>
                                              {tool.resourcesAccessed.map((resource, idx) => (
                                                <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'info.dark' }}>
                                                  • {resource.uri.split('/').pop() || resource.uri}
                                                </Typography>
                                              ))}
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    ))}

                                    {/* Expandable Technical Details */}
                                    {message.toolsUsed.length > 0 && (
                                      <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                        <MCPToolDetails tools={message.toolsUsed} />
                                      </Box>
                                    )}
                                  </Box>
                                )}

                                {/* Prompt Display for Assistant Messages */}
                                {message.role === 'assistant' && message.fullPrompt && (
                                  <PromptDisplay fullPrompt={message.fullPrompt} />
                                )}

                                {message.timestamp && (
                                  <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.7 }}>
                                    {formatTimestamp(message.timestamp)}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Paper>
                          {message.role === 'user' && (
                            <Box sx={{ ml: 1 }}>
                              <UserAvatar user={user} size={40} />
                            </Box>
                          )}
                        </Box>
                      )))
                    )}
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Mobile Bottom Toolbar */}
                  {isMobile && (
                    <>
                      <Divider />
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-around',
                        p: 1,
                        bgcolor: 'background.paper',
                        borderTop: 1,
                        borderColor: 'divider'
                      }}>
                        {/* Note-taking Mode */}
                        <Tooltip title={noteTakingMode ? "Note-taking is active" : "Enable note-taking"}>
                          <IconButton
                            onClick={() => setNoteTakingMode(!noteTakingMode)}
                            size="small"
                            sx={{
                              color: noteTakingMode ? '#ffeb3b' : 'text.secondary',
                              bgcolor: noteTakingMode ? 'rgba(255, 235, 59, 0.1)' : 'transparent',
                            }}
                          >
                            {noteTakingMode ? <NoteIcon fontSize="small" /> : <NoteAddIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>

                        {/* Prompt Selector */}
                        {availablePrompts.length > 0 && (
                          <Tooltip title="Select AI Prompt">
                            <IconButton
                              onClick={() => setShowPromptSelector(true)}
                              size="small"
                              sx={{ color: 'text.secondary' }}
                            >
                              <AgentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Manage Prompts */}
                        <Tooltip title="Manage Prompts">
                          <IconButton
                            onClick={() => navigate('/faana-agents')}
                            size="small"
                            sx={{ color: 'text.secondary' }}
                          >
                            <ManagePromptsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Voice Controls */}
                        {userPreferences?.voice?.enabled && (
                          <>
                            <Tooltip title={activeMicMode ? "Active Mic ON" : "Enable Active Mic"}>
                              <IconButton
                                onClick={() => handleActiveMicToggle()}
                                size="small"
                                sx={{
                                  color: activeMicMode ? 'error.main' : 'text.secondary',
                                  bgcolor: activeMicMode ? 'rgba(255, 0, 0, 0.1)' : 'transparent',
                                }}
                              >
                                {activeMicMode ? <MicIcon fontSize="small" /> : <MicOffIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>

                            <Tooltip title={voiceResponseMode ? "Voice Response ON" : "Enable Voice Response"}>
                              <IconButton
                                onClick={() => handleVoiceResponseToggle()}
                                size="small"
                                sx={{
                                  color: voiceResponseMode ? 'primary.main' : 'text.secondary',
                                  bgcolor: voiceResponseMode ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                                }}
                              >
                                {voiceResponseMode ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {/* Settings */}
                      </Box>
                    </>
                  )}

                  {/* Input Area */}
                  <Divider />
                  <Box sx={{ p: 2, pb: isMobile ? 9 : 2, bgcolor: 'background.paper' }}>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                      </Alert>
                    )}
                    {noteTakingMode && (
                      <Alert
                        severity="info"
                        icon={<NoteIcon />}
                        sx={{ mb: 2 }}
                      >
                        Note-taking is active. Your conversation is being saved as a permanent note for {entityName}.
                      </Alert>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Ask about leadership growth, team dynamics, or workplace challenges..."
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={sendingMessage}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                          },
                        }}
                      />
                      {streamingMessageId ? (
                        <IconButton
                          onClick={handleStopStreaming}
                          sx={{ color: 'error.main' }}
                          title="Stop generating"
                        >
                          <StopCircleIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          color="primary"
                          onClick={sendMessage}
                          disabled={!inputMessage.trim() || sendingMessage}
                        >
                          <SendIcon />
                        </IconButton>
                      )}
                    </Box>

                    {/* Voice Status Indicator */}
                    {(activeMicMode || voiceStatus === 'generating' || voiceStatus === 'speaking') && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: voiceStatus === 'recording' ? 'error.main' :
                                      voiceStatus === 'listening' ? 'warning.main' :
                                      voiceStatus === 'speaking' || voiceStatus === 'generating' ? 'info.main' : 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        {voiceStatus === 'listening' && (
                          <>
                            <HearingIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Listening for "Hey Coach"...
                            </Typography>
                          </>
                        )}
                        {voiceStatus === 'recording' && (
                          <>
                            <MicIcon sx={{ color: 'error.main', fontSize: 20, animation: 'pulse 1s infinite' }} />
                            <Typography variant="caption" sx={{ color: 'text.primary', flex: 1 }}>
                              Recording: {voiceTranscriptBuffer || 'Speak now...'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Say "send now" or "clear message"
                            </Typography>
                          </>
                        )}
                        {voiceStatus === 'generating' && (
                          <>
                            <CircularProgress size={16} sx={{ color: 'info.main' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                              Generating speech...
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                voiceService.stopSpeaking();
                                setVoiceStatus(activeMicMode ? 'listening' : 'idle');
                              }}
                              sx={{ p: 0.5 }}
                              title="Stop speech"
                            >
                              <StopCircleIcon sx={{ fontSize: 18, color: 'error.main' }} />
                            </IconButton>
                          </>
                        )}
                        {voiceStatus === 'speaking' && (
                          <>
                            <VolumeUpIcon sx={{ color: 'info.main', fontSize: 20 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                              AI is speaking...
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                voiceService.stopSpeaking();
                                setVoiceStatus(activeMicMode ? 'listening' : 'idle');
                              }}
                              sx={{ p: 0.5 }}
                              title="Stop speech"
                            >
                              <StopCircleIcon sx={{ fontSize: 18, color: 'error.main' }} />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Chat Menu */}
        <Menu
          anchorEl={chatMenuAnchor}
          open={Boolean(chatMenuAnchor)}
          onClose={() => {
            setChatMenuAnchor(null);
            setSelectedChat(null);
          }}
        >
          <MenuItem
            onClick={() => {
              const chat = chats.find(c => c.chatId === selectedChat);
              setRenamingChat(selectedChat);
              setNewChatTitle(chat?.title || '');
              setChatMenuAnchor(null);
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Rename
          </MenuItem>
          <MenuItem onClick={handleDeleteChat}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Rename Dialog */}
        <Dialog
          open={Boolean(renamingChat)}
          onClose={() => {
            setRenamingChat(null);
            setNewChatTitle('');
          }}
        >
          <DialogTitle>Rename Chat</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              value={newChatTitle}
              onChange={(e) => setNewChatTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRenameChat();
                }
              }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setRenamingChat(null);
              setNewChatTitle('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleRenameChat} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Prompt Selector Dialog */}
        <Dialog
          open={showPromptSelector}
          onClose={() => setShowPromptSelector(false)}
          maxWidth="md"
          fullWidth
          sx={{
            zIndex: 1400, // Higher than Drawer (1300) to appear on top
          }}
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Select an Agent
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.primary', opacity: 0.8 }}>
              Choose how Faana AI should behave and respond
            </Typography>
          </DialogTitle>
          <DialogContent>
            {loadingPrompts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List>
                {availablePrompts.map((prompt) => (
                  <ListItem
                    key={prompt.id}
                    button
                    selected={selectedPrompt?.id === prompt.id}
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      setShowPromptSelector(false);
                    }}
                    sx={{
                      border: 1,
                      borderColor: selectedPrompt?.id === prompt.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {prompt.name}
                          </Typography>
                          {prompt.isDefault && (
                            <Chip label="Default" size="small" color="primary" />
                          )}
                          <Chip
                            label={`v${prompt.version}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, color: 'text.primary', opacity: 0.9 }}>
                            {prompt.description}
                          </Typography>
                          {prompt.metadata?.model && (
                            <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7 }}>
                              Model: {prompt.metadata.model}
                              {prompt.metadata.temperature !== undefined &&
                                ` • Temperature: ${prompt.metadata.temperature}`}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPromptSelector(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>


        </>
        );

        // Wrap content based on mode
        if (fullPage) {
          return (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
              margin: 0,
              padding: 0,
              overflow: 'hidden'
            }}>
              {chatContent}
            </Box>
          );
        } else {
          return (
            <SwipeableDrawer
              anchor="right"
              open={open}
              onClose={() => setOpen(false)}
              onOpen={() => setOpen(true)}
              disableSwipeToOpen={false}
              sx={{
                zIndex: 1300, // Higher than Material-UI AppBar default (1100)
              }}
              PaperProps={{
                sx: {
                  width: {
                    xs: '100%', // Full width on mobile
                    sm: '85%',  // 85% on small tablets
                    md: 600,    // Fixed 600px on desktop
                    lg: 700     // Fixed 700px on large screens
                  },
                  height: '100%',
                  zIndex: 1300, // Ensure paper is also above other elements
                }
              }}
            >
              {chatContent}
            </SwipeableDrawer>
          );
        }
      })()}
      {/* Chrome Warning Dialog */}
      <Dialog
        open={showChromeWarning}
        onClose={() => setShowChromeWarning(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: 'warning.main' }} />
          Speech Recognition Not Available
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Voice input features including the &quot;Hey Coach&quot; wake word require Chrome or Edge.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The Web Speech Recognition API is not reliably supported in this browser.
            Voice responses (text-to-speech) will still work — only microphone input requires Chrome or Edge.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChromeWarning(false)} color="primary">
            Understood
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChatBot;